import { z } from 'zod';

// ─── Status enum ────────────────────────────────────────────────────────────

const statusEnum = z.enum(['ACTIVE', 'DEPRECATED', 'PROPOSED']);

// ─── Standard Term ───────────────────────────────────────────────────────────

/**
 * Zod schema for StandardTermUpsertRequest.
 * - korean: required
 * - englishShort: required, letters/digits/underscore only (BE @Pattern)
 * - englishFull, description, domain: optional
 * - source: optional string (defaults to MANUAL on BE)
 * - status: optional enum (defaults to ACTIVE on BE)
 */
export const standardTermSchema = z.object({
  korean: z
    .string()
    .min(1, '한글 용어를 입력해주세요')
    .max(255, '한글 용어는 255자 이내로 입력해주세요'),
  englishShort: z
    .string()
    .min(1, '영문 약어를 입력해주세요')
    .max(128, '영문 약어는 128자 이내로 입력해주세요')
    .regex(/^[A-Za-z0-9_]+$/, '영문 약어는 영문/숫자/밑줄(_)만 입력 가능합니다 (예: CK01_CTLNO)'),
  englishFull: z
    .string()
    .max(255, '영문 풀네임은 255자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  description: z
    .string()
    .max(1000, '설명은 1000자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  domain: z
    .string()
    .max(128, '도메인은 128자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  source: z
    .string()
    .max(64, '소스는 64자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  status: statusEnum.default('ACTIVE'),
});

export type StandardTermFormValues = z.infer<typeof standardTermSchema>;

// ─── Forbidden Word ──────────────────────────────────────────────────────────

/**
 * Zod schema for ForbiddenWordUpsertRequest.
 * - bad: required
 * - good: required
 * - reason, source: optional
 * - status: optional enum
 */
export const forbiddenWordSchema = z.object({
  bad: z
    .string()
    .min(1, '금칙어를 입력해주세요')
    .max(128, '금칙어는 128자 이내로 입력해주세요'),
  good: z
    .string()
    .min(1, '권장어를 입력해주세요')
    .max(255, '권장어는 255자 이내로 입력해주세요'),
  reason: z
    .string()
    .max(500, '사유는 500자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  source: z
    .string()
    .max(64, '소스는 64자 이내로 입력해주세요')
    .optional()
    .or(z.literal('')),
  status: statusEnum.default('ACTIVE'),
});

export type ForbiddenWordFormValues = z.infer<typeof forbiddenWordSchema>;

// ─── Terminology Checker (enhance-biz #661) ─────────────────────────────────

/**
 * Zod schema for LintRequest (client-side mirror of BE @NotBlank @Size(max=65536)).
 */
export const lintRequestSchema = z.object({
  text: z
    .string()
    .min(1, '검사할 텍스트를 입력해주세요')
    .max(65536, '검사 텍스트는 64KB 를 초과할 수 없습니다'),
});

export type LintRequestFormValues = z.infer<typeof lintRequestSchema>;

const lintViolationSchema = z.object({
  line: z.number(),
  column: z.number(),
  matched: z.string(),
  suggestion: z.string(),
  reason: z.string().nullable().optional(),
  source: z.string(),
});

export const lintReportResponseSchema = z.object({
  violations: z.array(lintViolationSchema),
  summary: z.object({
    forbiddenCount: z.number(),
    scannedLines: z.number(),
    scannedChars: z.number(),
    standardTermChecked: z.boolean(),
  }),
});
